<template>
  <div
    v-show="modalActive"
    ref="modalContainer"
    role="dialog"
    class="fixed inset-0 z-[101] flex h-full w-full items-center justify-center overflow-y-auto bg-grey-900/25 px-4 py-8 backdrop-blur-[8px] lg:py-16"
    @click="clickOutsideModal"
    @keydown.tab="handleTab"
    @keydown.esc="$emit('closeModal')"
  >
    <div
      v-if="infoBox"
      class="z-40 flex max-w-sm flex-col gap-3 rounded-xl bg-sky-100 p-4 shadow-md md:flex-row md:items-start md:gap-4"
    >
      <div class="flex gap-3">
        <IconAlertInfo
          class="fill-alert-info-icon mt-1 h-4 w-4 shrink-0 md:h-6 md:w-6"
        />
        <div>
          <div
            class="mb-2 text-sm font-bold uppercase md:text-lg"
            v-html="infoBox.title"
          ></div>
          <div
            class="text-xs font-light md:text-sm [&>*]:text-xs md:[&>*]:text-sm"
            v-html="infoBox.description"
          ></div>
        </div>
      </div>
      <button
        ref="closeButton"
        class="w-max shrink-0 text-left text-link md:text-grey-700"
        @click="$emit('closeModal')"
      >
        <span class="ml-7 mr-2 inline text-sm font-light md:hidden"
          >Dismiss</span
        >
        <IconCloseModal
          class="inline h-4 w-4 shrink-0 fill-current md:h-5 md:w-5"
        />
      </button>
    </div>
    <div
      v-else
      class="relative z-40 my-auto w-full rounded-3xl border border-grey-900/25 bg-grey-50 shadow-lg"
      :class="customWidth ? customWidth : 'max-w-screen-sm'"
      v-bind="$attrs"
    >
      <div
        class="flex w-full justify-between px-4 pt-4"
        :class="title ? 'border-b border-grey-900/25 pb-3' : 'border-b-0 pb-0'"
      >
        <h3 class="my-auto">{{ title }}</h3>
        <button
          ref="closeButton"
          aria-label="Close"
          type="button"
          @click="$emit('closeModal')"
        >
          <IconCloseModal class="h-4 w-4 shrink-0 fill-grey-900" />
        </button>
      </div>
      <div :class="customSlotClass ? customSlotClass : 'px-8 pb-8'">
        <slot />
      </div>
    </div>
    <button
      ref="closeBackground"
      class="absolute left-0 top-0 h-full w-full opacity-0"
      role="button"
      @click="$emit('closeModal')"
    >
      <span class="sr-only">Close popup</span>
    </button>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
const props = defineProps<{
  modalActive: boolean;
  infoBox?: {
    title: string;
    description: string;
  };
  customWidth?: string;
  title?: string;
  customSlotClass?: string;
}>();

const emit = defineEmits(["closeModal"]);

const modalContainer = ref<HTMLElement | null>(null);
const closeButton = ref();
const closeBackground = ref();
const modalActive = computed(() => props.modalActive);

watch(modalActive, () => {
  if (modalActive.value) {
    nextTick(() => {
      document.body.classList.add("overflow-hidden");
      closeButton.value.focus();
    });
  } else {
    document.body.classList.remove("overflow-hidden");
  }
});

// Only close if clicking the container, not the modal div itself
const clickOutsideModal = (e: Event) => {
  if (modalContainer.value === e.target) {
    emit("closeModal");
  }
};

const handleTab = (e: KeyboardEvent) => {
  if (!modalActive.value) return;
  restrictTab(e, closeButton.value, closeBackground.value);
};
</script>
